import React, { useMemo } from 'react';

import { PublicLessonsBlockClientData } from '@yandex-int/k-common/typings/api/models/main-page';
import { LessonCardProps, LessonPromoCard } from '@edu-frontend/modules/src/entities/lesson';
import { LessonCardsGroup } from '@edu-frontend/modules/src/features/lessonCardsGroup';
import { useIntl } from 'react-intl';
import ym from 'react-yandex-metrika';
import { useHistory } from 'react-router';

import { LessonStatusProps } from '@edu-frontend/modules/src/entities/lesson/ui/LessonStatus/LessonStatus';
import { useGeneratePath, useMe, useStaticUrls } from 'platform-components';
import { IconClock } from '@edu-frontend/modules/src/shared/ui/Icon';

import './public-lessons.scss';

interface PublicLessonsWidgetProps {
    data: PublicLessonsBlockClientData;
}
export const PublicLessonsWidget = ({ data }: PublicLessonsWidgetProps) => {
    const intl = useIntl();
    const history = useHistory();
    const staticUrls = useStaticUrls();
    const getRouterPath = useGeneratePath();
    const me = useMe();

    const header = data.additional_info.view_settings.title;
    const description = data.additional_info.view_settings.description;
    const items: LessonCardProps[] = useMemo(() => {
        return data.additional_info.lessons.map((lesson) => {
            return {
                onClick: () => {
                    ym('reachGoal', 'teacher_view_as_student', {
                        teacher_id: me?.id,
                        clessonId: lesson.id,
                    });
                    const url = `${getRouterPath(staticUrls.classroomPublicLessonRun, {
                        lessonId: lesson.id,
                    })}`;
                    window.open(url, '_blank', 'noopener');
                },
                methodologicalGoal: lesson.meta.methodological_goal
                    ? intl.formatMessage({
                          id: `lesson.lessonMeta.methodologicalGoals.${lesson.meta.methodological_goal}`,
                      })
                    : undefined,
                description: intl.formatMessage(
                    { id: 'library.prepLessons.problemsCount' },
                    { count: lesson.problems_count }
                ),
                id: String(lesson.id),
                status: 'promo' as const,
                methodologicalGoalColor: lesson.meta.methodological_goal
                    ? (intl.formatMessage({
                          id: `lesson.lessonMeta.methodologicalGoals.${lesson.meta.methodological_goal}.color`,
                      }) as LessonStatusProps['color'])
                    : undefined,
                name: lesson.name,
                timeText: lesson.meta.duration
                    ? intl.formatMessage({ id: 'lesson-meta.duration' }, { duration: lesson.meta.duration })
                    : undefined,
                timeIcon: <IconClock />,
                imgSrc: lesson.meta.header?.background_image as string,
            };
        });
    }, [data.additional_info, intl, getRouterPath, staticUrls.classroomPublicLessonRun, me?.id]);

    const extraCard = useMemo(() => {
        const extraCardSettings = data.additional_info.view_settings.extra_card_settings;
        if (!extraCardSettings) {
            return undefined;
        }
        return [
            <LessonPromoCard
                buttonText={extraCardSettings.button.text}
                buttonView={extraCardSettings.button.view}
                className="public-lessons__extra-card"
                description={extraCardSettings.description}
                imgSrc={extraCardSettings.img}
                onButtonClick={() => {
                    ym('reachGoal', 'public_lessons_widget_extra_card', {
                        id: data.id,
                        title: data.additional_info.view_settings.title,
                        description: data.additional_info.view_settings.description,
                    });

                    const url = extraCardSettings.button.action.link;
                    if (!url) {
                        return;
                    }

                    const isExternalUrl = new RegExp(/^https?:\/\//i).test(url);
                    if (isExternalUrl) {
                        window.open(url, '_blank', 'noopener');
                    } else {
                        history.push(url);
                    }
                }}
            />,
        ];
    }, [data.additional_info.view_settings, history, data.id]);

    if (items.length === 0) {
        return null;
    }

    return <LessonCardsGroup description={description} extraCardsEnd={extraCard} header={header} items={items} />;
};
